// React and Hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Helpers
import { transformRoute, useTranslate } from "helpers";

// Services/API
import { DataWithPagination } from "services/api";
import { getTollsAPI } from "services/api/calculations/toll/api";
import { TollRow, TollSearchFormValues } from "services/api/calculations/toll/types";
import { initialDataWithPagination } from "services/api/consts";

// Services/Search
import { Pagination } from "services/search";

//types
import { TollSearchPageCmp, TollSearchPageProps } from "./types";

// UI Components
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { NoResults } from "ui/components/NoResults";
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";
import TabMenu from "../TabMenu/TabMenu";

// Constants
import {
  CALCULATION_TOLL_CREATE_PAGE_TITLE,
  CALCULATION_TOLL_SEARCH_PAGE_TITLE,
} from "ui/components/PageTitle/consts";

// Custom Components
import TollSearchFilters from "./components/TollSearchFilters/TollSearchFilters";
import TollSearchResults from "./components/TollSearchResults/TollSearchResults";
import TollEditCard from "../TollEditCard/TollEditCard";
import { Routes } from "ui/modules/calculation/navigation";


const TollSearchPage: TollSearchPageCmp = (
  props: TollSearchPageProps
) => {
  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<TollRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [tollId, setTollId] = useState<string | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    name?: string | null
  ) => {
    setIsLoading(true);
    try {
      const results = await getTollsAPI(pagination, name);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    navigate(
      transformRoute(Routes.CalculationTollDetailPageRouter, {
        tollId: id,
      })
    );
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setTollId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleSearchChange = useCallback(
    (searchFormValues: TollSearchFormValues) => {
      fetchSearchResult(searchResults.pagination, searchFormValues.search);
    },
    []
  );

  const handleEditSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
    setTollId(null);
    setShowAddNewModal(false);
  }, []);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <PageHeader>
            <PageTitle
              title={CALCULATION_TOLL_SEARCH_PAGE_TITLE.text}
              icon={CALCULATION_TOLL_SEARCH_PAGE_TITLE.icon}
              iconColor={CALCULATION_TOLL_SEARCH_PAGE_TITLE.iconColor}
            />
            <Breadcrumb />
          </PageHeader>
          <TabMenu />
        </div>
        <div className="px-0 pt-2 mx-[-1px] xl:px-6">
          <TollSearchFilters
            onSearchChange={handleSearchChange}
            onAddNew={handleAddNew}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults
              message={t("admin.calculation.setting.toll.search.noresults")}
            />
          ) : (
            <TollSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
            />
          )}
        </div>
      </div>
      {showAddNewModal && (
        <Modal show={showAddNewModal} onHide={() => setShowAddNewModal(false)}>
          <ModalHeader
            title={CALCULATION_TOLL_CREATE_PAGE_TITLE.text}
            onHide={() => setShowAddNewModal(false)}
          />
          <ModalBody>
            <TollEditCard
              tollId={tollId}
              onSuccess={handleEditSuccess}
            />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

TollSearchPage.route = Routes.CalculationTollSearchPageRouter;

export default TollSearchPage;
